import React, { useEffect, useState } from 'react';

import {

  Dialog,

  DialogTitle,

  DialogContent,

  DialogActions,

  Button,

  Box,

  Typography,

  IconButton,

  Divider,

  Alert,

  useTheme,

  alpha,

} from '@mui/material';

import { Close as CloseIcon } from '@mui/icons-material';

import { CreateDocumentRevisionForm, RevisionName, Party } from '../types';

import RevisionForm from './forms/RevisionForm';

import RevisionWorkflowForm from './forms/RevisionWorkflowForm';

import AuxiliaryFilesWidget, { AuxFileDraft } from './forms/AuxiliaryFilesWidget';

import {

  RevisionFormValues,

  RevisionWorkflowFormValues,

  buildDefaultRevisionFormValues,

  buildDefaultRevisionWorkflowFormValues,

  getRevisionFormMissingFields,

  getRevisionWorkflowFormMissingFields,

  labelsForDocumentFields,

} from './forms/formTypes';

import { useFormFieldErrors } from '../hooks/useFormFieldErrors';

import FormValidationAlert from './FormValidationAlert';

import { buildCreateRevisionPayload, uploadAuxDraftsForRevision } from './forms/workflowUtils';



const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };



/** Combined revision + workflow dialog for MDR grid and project documents quick-add. */

export interface RevisionWithWorkflowDialogProps {

  open: boolean;

  onClose: () => void;

  projectId: number;

  documentId: number;

  documentTitle: string;

  revisionNames: RevisionName[];

  parties: Party[];

  onSubmit: (form: CreateDocumentRevisionForm) => Promise<{ revision_id: number } | void>;

  loading?: boolean;

  nextRevisionCounter?: number;

  initialForm?: Partial<CreateDocumentRevisionForm> | null;

  dialogTitle?: string;

  submitLabel?: string;

  submittingLabel?: string;

  lockRevisionIdentity?: boolean;

  lockRevisionDates?: boolean;

  disabled?: boolean;

}



const RevisionWithWorkflowDialog: React.FC<RevisionWithWorkflowDialogProps> = ({

  open,

  onClose,

  projectId,

  documentId,

  documentTitle,

  revisionNames,

  parties,

  onSubmit,

  loading = false,

  nextRevisionCounter,

  initialForm = null,

  dialogTitle = 'Create Document Revision',

  submitLabel = 'Create Revision',

  submittingLabel = 'Creating...',

  lockRevisionIdentity = false,

  lockRevisionDates = false,

  disabled = false,

}) => {

  const theme = useTheme();

  const isDark = theme.palette.mode === 'dark';

  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';

  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';



  const [revisionForm, setRevisionForm] = useState<RevisionFormValues>(buildDefaultRevisionFormValues());

  const [workflowForm, setWorkflowForm] = useState<RevisionWorkflowFormValues>(

    buildDefaultRevisionWorkflowFormValues()

  );

  const [auxNewEntries, setAuxNewEntries] = useState<AuxFileDraft[]>([]);

  const [partialError, setPartialError] = useState<string | null>(null);

  const fieldErrors = useFormFieldErrors();

  const [submitting, setSubmitting] = useState(false);



  useEffect(() => {

    if (open) {

      const defaultRevision = buildDefaultRevisionFormValues();

      setRevisionForm({

        revision_name_id: initialForm?.revision_name_id ?? defaultRevision.revision_name_id,

        step: initialForm?.step ?? defaultRevision.step,

        start_date: initialForm?.start_date ?? defaultRevision.start_date,

        expected_issue_date: initialForm?.expected_issue_date ?? defaultRevision.expected_issue_date,

      });

      setWorkflowForm({

        author_party_id: initialForm?.author_party_id,

        checkers: initialForm?.checkers ? [...initialForm.checkers] : [],

        approvers: initialForm?.approvers ? [...initialForm.approvers] : [],

      });

      setAuxNewEntries([]);

      setPartialError(null);

    }

  }, [open, initialForm]);



  const busy = loading || submitting;



  const handleSubmit = async () => {

    if (busy || disabled) return;

    const missing = [

      ...getRevisionFormMissingFields(revisionForm),

      ...getRevisionWorkflowFormMissingFields(workflowForm),

    ];

    if (missing.length > 0) {

      fieldErrors.showMissing(missing, labelsForDocumentFields(missing));

      return;

    }

    setSubmitting(true);

    setPartialError(null);



    try {

      const result = await onSubmit(buildCreateRevisionPayload(documentId, revisionForm, workflowForm));

      if (result === undefined || result === null) {
        setPartialError('Failed to create revision. Please try again.');
        return;
      }

      const revisionId =
        typeof result === 'object' && 'revision_id' in result
          ? result.revision_id
          : undefined;

      if (!revisionId) {
        setPartialError('Failed to create revision. Please try again.');
        return;
      }

      if (auxNewEntries.length > 0) {

        try {

          await uploadAuxDraftsForRevision(projectId, documentId, revisionId, auxNewEntries);

        } catch (auxErr: any) {

          const count = auxNewEntries.length;

          const auxMsg =

            auxErr.response?.data?.error ||

            auxErr.message ||

            'Failed to upload auxiliary files';

          setPartialError(`Revision created. Failed to upload ${count} aux file(s): ${auxMsg}`);

          return;

        }

      }

      onClose();

    } catch (err: any) {

      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to create revision. Please try again.';
      setPartialError(message);

    } finally {

      setSubmitting(false);

    }

  };



  return (

    <Dialog

      open={open}

      onClose={onClose}

      maxWidth="md"

      fullWidth

      PaperProps={{

        sx: {

          bgcolor: surfaceBg,

          border: `1px solid ${panelBorder}`,

          borderRadius: 1,

        },

      }}

    >

      <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5 }}>

        <Box display="flex" alignItems="center" justifyContent="space-between">

          <Box>

            <Typography

              variant="h6"

              sx={{

                fontWeight: 600,

                letterSpacing: '-0.01em',

                color: isDark ? '#ffffff' : NAUTILUS.deepOcean,

              }}

            >

              {dialogTitle}

            </Typography>

            <Typography

              variant="body2"

              sx={{

                color: isDark ? '#b0bec5' : '#546e7a',

                fontFamily: 'monospace',

              }}

            >

              {documentTitle}

            </Typography>

          </Box>

          <IconButton onClick={onClose}>

            <CloseIcon />

          </IconButton>

        </Box>

      </DialogTitle>

      <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />

      <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>

        {partialError && (

          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPartialError(null)}>

            {partialError}

          </Alert>

        )}

        <RevisionForm

          value={revisionForm}

          onChange={setRevisionForm}

          revisionNames={revisionNames}

          nextRevisionCounter={nextRevisionCounter}

          lockIdentity={lockRevisionIdentity}

          lockDates={lockRevisionDates}

          invalid={fieldErrors.invalid}

          onFieldEdit={fieldErrors.clearField}

        />

        <AuxiliaryFilesWidget

          newEntries={auxNewEntries}

          onNewEntriesChange={setAuxNewEntries}

          disabled={disabled || busy}

        />

        <Divider sx={{ my: 3 }} />

        <Typography

          sx={{

            fontSize: '0.65rem',

            fontWeight: 600,

            letterSpacing: '0.08em',

            textTransform: 'uppercase',

            color: isDark ? '#b0bec5' : '#546e7a',

            mb: 2,

          }}

        >

          Revision Workflow

        </Typography>

        <RevisionWorkflowForm

          value={workflowForm}

          onChange={setWorkflowForm}

          parties={parties}

          invalid={fieldErrors.invalid}

          onFieldEdit={fieldErrors.clearField}

        />

      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>

        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>

          Cancel

        </Button>

        <Button

          variant="contained"

          onClick={() => void handleSubmit()}

          disabled={busy || disabled}

          sx={{

            bgcolor: NAUTILUS.sand,

            color: NAUTILUS.deepOcean,

            fontWeight: 600,

            '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85) },

            '&:disabled': { bgcolor: alpha(NAUTILUS.sand, 0.3) },

          }}

        >

          {busy ? submittingLabel : submitLabel}

        </Button>

      </DialogActions>

    </Dialog>

  );

};



export default RevisionWithWorkflowDialog;

